import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { API_BASE_URL } from '../../config/api.js';
import styles from "../../styles/Auth.module.css";

export default function Register() {
  const { handleRegister } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // helpers dla własnych komunikatów
  const setValidity = (e, msg) => e.target.setCustomValidity(msg || "");
  const clearValidity = (e) => e.target.setCustomValidity("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Hasła muszą być identyczne");
      return;
    }
    if (!inviteCode) {
      setError("Kod zaproszenia jest wymagany.");
      return;
    }
    if (!acceptedTerms) {
      setError("Musisz zaakceptować Regulamin serwisu.");
      return;
    }

    setLoading(true);
    try {
      // 1. Verify invite code
      const res = await fetch(`${API_BASE_URL}/api/auth/verify-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ inviteCode }),
      });

      if (!res.ok) {
        throw new Error("Nieprawidłowy kod zaproszenia.");
      }

      const { tenantId } = await res.json();

      // 2. Register user in Firebase
      await handleRegister(email, password);

      // 3. Get current user's UID
      const user = getAuth().currentUser;
      const uid = user?.uid;
      if (!uid) throw new Error("Nie udało się uzyskać UID użytkownika.");

      // 4. Send UID + tenantId to backend
      await fetch(`${API_BASE_URL}/api/auth/save-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid, tenantId, inviteCode }),
      });

      navigate("/documents");
    } catch (err) {
      console.error(err);
      setError(err.message || "Błąd rejestracji. Spróbuj ponownie.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.authBg}>
      <div className={styles.mainContent}>
        <Link to="/">
          <img src="/logo2.png" alt="Paperly logo" className={styles.authLogoStandalone} />
        </Link>
        <form onSubmit={onSubmit} className={styles.authForm} autoComplete="on" aria-label="Rejestracja">
          <div className={styles.authHeader}>
            <h1 className={styles.authTitle}>Załóż konto</h1>
            <p className={styles.authSubtitle}>Utwórz nowe konto, aby zacząć automatyzację.</p>
          </div>

          <div className={styles.inputGroup}>
            <input
              id="inviteCode"
              value={inviteCode}
              onChange={(e) => { clearValidity(e); setInviteCode(e.target.value); }}
              onInvalid={(e) => setValidity(e, "Wpisz kod zaproszenia. Jeżeli jeszcze go nie posiadasz, skontaktuj się z administratorem.")}
              required
              className={styles.inputField}
              placeholder=" "
            />
            <label htmlFor="inviteCode" className={styles.inputLabel}>Kod zaproszenia</label>
          </div>

          <div className={styles.inputGroup}>
            <input
              id="email"
              value={email}
              onChange={(e) => { clearValidity(e); setEmail(e.target.value); }}
              onInvalid={(e) => setValidity(e, "Wpisz poprawny adres e‑mail.")}
              type="email"
              required
              className={styles.inputField}
              autoComplete="email"
              placeholder=" "
            />
            <label htmlFor="email" className={styles.inputLabel}>Adres e-mail</label>
          </div>

          <div className={styles.inputGroup}>
            <input
              id="password"
              value={password}
              onChange={(e) => { clearValidity(e); setPassword(e.target.value); }}
              onInvalid={(e) => setValidity(e, "Wpisz hasło.")}
              type="password"
              required
              className={styles.inputField}
              autoComplete="new-password"
              placeholder=" "
            />
            <label htmlFor="password" className={styles.inputLabel}>Hasło</label>
          </div>

          <div className={styles.inputGroup}>
            <input
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => { clearValidity(e); setConfirmPassword(e.target.value); }}
              onInvalid={(e) => setValidity(e, "Powtórz hasło.")}
              type="password"
              required
              className={styles.inputField}
              autoComplete="new-password"
              placeholder=" "
            />
            <label htmlFor="confirmPassword" className={styles.inputLabel}>Powtórz hasło</label>
          </div>

          <div className={styles.termsRow}>
            <input
              id="acceptTerms"
              type="checkbox"
              className={styles.termsCheckbox}
              checked={acceptedTerms}
              onChange={(e) => { e.target.setCustomValidity(""); setAcceptedTerms(e.target.checked); }}
              onInvalid={(e) => setValidity(e, "Zaakceptuj Regulamin serwisu.")}
              required
            />
            <label htmlFor="acceptTerms" className={styles.termsLabel}>
              Akceptuję <Link to="/regulamin" className={styles.termsLink}>Regulamin serwisu</Link>
            </label>
          </div>

          {error && <div className={styles.authError}>{error}</div>}
          
          <button type="submit" className={styles.authButton} disabled={loading}>
            {loading ? "Tworzenie konta..." : "Zarejestruj się"}
          </button>
          
          <div className={styles.authFooter}>
            <span>Masz już konto? </span>
            <Link to="/login" className={styles.authLink}>Zaloguj się</Link>
          </div>
        </form>
      </div>
      <footer className={styles.authPageFooter}>
        <Link to="/" className={styles.authPageFooterLink}>Strona główna</Link>
        <span className={styles.authPageFooterSeparator}>•</span>
        <Link to="/polityka-prywatnosci" className={styles.authPageFooterLink}>Polityka Prywatności</Link>
        <span className={styles.authPageFooterSeparator}>•</span>
        <Link to="/regulamin" className={styles.authPageFooterLink}>Regulamin</Link>
      </footer>
    </div>
  );
} 